import React, { useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { RxAvatar } from "react-icons/rx";
import { HiOutlineUpload } from "react-icons/hi";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";

const Signup = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    setAvatar(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error("Please fill all required fields"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const newForm = new FormData();
      if (avatar) newForm.append("file", avatar);
      newForm.append("name", name);
      newForm.append("email", email);
      newForm.append("password", password);
      const res = await axios.post(`${server}/user/create-user`, newForm, config);
      toast.success(res.data.message);
      setName(""); setEmail(""); setPassword(""); setAvatar(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-black flex-col justify-between p-14">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo (10).png" alt="Logo" className="h-10 w-auto invert brightness-200 object-contain" />
        </Link>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-accent-500 mb-4">Join Us</p>
          <h1 className="text-4xl font-bold text-white leading-snug mb-6">
            Start your<br />wellness journey.
          </h1>
          <p className="text-secondary-400 text-sm leading-relaxed max-w-sm">
            Create your account to get access to exclusive wellness products, track your orders, and enjoy a personalised shopping experience.
          </p>
        </div>
        <p className="text-secondary-600 text-xs">copyright 2025 Positive Moments</p>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 relative">
        <button onClick={() => navigate("/")} className="absolute top-6 left-6 flex items-center gap-2 text-sm text-secondary-500 hover:text-black transition-colors">
          <HiOutlineArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo (10).png" alt="Logo" className="h-10 w-auto object-contain" />
          </div>

          <p className="text-xs uppercase tracking-[0.2em] text-accent-600 mb-2">Get Started</p>
          <h2 className="text-3xl font-bold text-black mb-2">Create account</h2>
          <p className="text-secondary-500 text-sm mb-8">
            Already have an account?{" "}
            <Link to="/login" className="text-black font-semibold underline underline-offset-2 hover:text-accent-600 transition-colors">
              Sign in
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 border-2 border-secondary-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {avatar ? (
                  <img src={URL.createObjectURL(avatar)} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <RxAvatar className="w-8 h-8 text-secondary-400" />
                )}
              </div>
              <label htmlFor="avatar-upload" className="flex items-center gap-2 px-3 py-2 border border-secondary-200 text-xs font-medium text-secondary-600 hover:border-black hover:text-black transition-colors cursor-pointer">
                <HiOutlineUpload className="w-3.5 h-3.5" />
                Upload photo
              </label>
              <input type="file" id="avatar-upload" accept=".jpg,.jpeg,.png" onChange={handleFileInputChange} className="sr-only" />
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Full Name</label>
              <input type="text" required placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Email</label>
              <input type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Password</label>
              <div className="relative">
                <input type={visible ? "text" : "password"} required placeholder="Min. 6 characters" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white" />
                <button type="button" onClick={() => setVisible(!visible)} className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-black transition-colors">
                  {visible ? <AiOutlineEye size={18} /> : <AiOutlineEyeInvisible size={18} />}
                </button>
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start gap-2">
              <input type="checkbox" id="terms" required className="mt-0.5 w-4 h-4 border-secondary-300 accent-black" />
              <label htmlFor="terms" className="text-xs text-secondary-500">
                I agree to the{" "}
                <Link to="/buyer-terms" className="text-black font-medium hover:text-accent-600 transition-colors">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" className="text-black font-medium hover:text-accent-600 transition-colors">Privacy Policy</Link>
              </label>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3.5 bg-black text-white text-sm font-semibold uppercase tracking-wider hover:bg-secondary-800 transition-colors disabled:opacity-50 mt-2">
              {loading ? "Creating account" : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
